import React, { useMemo } from 'react';
import {
  formatPeriod,
  WAHIS_INFUR_LABEL,
  type FastReportPeriod,
  type InfurOutbreakPoint,
} from '../../utils/fastReport/currentSituation';
import type { BeaconNewsItem } from './BeaconNewsPanel';

/** FAST row fields needed for the Now country summary. */
export interface CountryNowFastRow {
  id?: number;
  Year: number;
  Quarter: number;
  Region?: string;
  Country: string;
  Disease: string;
  Outbreaks?: string | number | null;
  Outbreak_Description?: string | null;
  Epidemiological_Information?: string | null;
  Surveillance?: number | string | null;
  Vaccination?: number | string | null;
  Vaccination_Doses?: number | string | null;
  Vaccination_Description?: string | null;
  Other_Info?: string | null;
  Source?: string | null;
}

interface CountryNowSummaryPanelProps {
  displayName: string;
  periodLabel: string;
  nowPeriods: FastReportPeriod[];
  fastRows: CountryNowFastRow[];
  infurPoints: InfurOutbreakPoint[];
  getDiseaseColor: (disease: string) => string;
  beaconNews?: BeaconNewsItem[];
  showBeaconNews?: boolean;
}

const DISEASE_ORDER = ['FMD', 'PPR', 'LSD', 'SPGP', 'RVF'];

function sortDiseases(a: string, b: string): number {
  const ia = DISEASE_ORDER.indexOf(a);
  const ib = DISEASE_ORDER.indexOf(b);
  if (ia !== -1 && ib !== -1) return ia - ib;
  if (ia !== -1) return -1;
  if (ib !== -1) return 1;
  return a.localeCompare(b);
}

function parseCount(raw: string | number | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(String(raw).replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function formatNewsDate(raw?: string | null): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function NoteBlock({ label, text }: { label: string; text?: string | null }) {
  const trimmed = (text || '').trim();
  if (!trimmed) return null;
  return (
    <div className="mt-1.5">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <p className="text-xs text-gray-700 whitespace-pre-wrap leading-snug mt-0.5">{trimmed}</p>
    </div>
  );
}

const CountryNowSummaryPanel: React.FC<CountryNowSummaryPanelProps> = ({
  displayName,
  periodLabel,
  nowPeriods,
  fastRows,
  infurPoints,
  getDiseaseColor,
  beaconNews = [],
  showBeaconNews = true,
}) => {
  const rowsByPeriod = useMemo(() => {
    const map = new Map<string, CountryNowFastRow[]>();
    for (const row of fastRows) {
      const key = formatPeriod({ year: Number(row.Year), quarter: Number(row.Quarter) });
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    // Prefer nowPeriods order (newest first)
    const orderedKeys = [
      ...nowPeriods.map(formatPeriod),
      ...Array.from(map.keys()).filter((k) => !nowPeriods.some((p) => formatPeriod(p) === k)),
    ];
    return orderedKeys
      .filter((k) => map.has(k))
      .map((key) => ({
        key,
        rows: (map.get(key) || []).slice().sort((a, b) => sortDiseases(a.Disease, b.Disease)),
      }));
  }, [fastRows, nowPeriods]);

  const region = fastRows.find((r) => r.Region)?.Region || null;

  const newsSection = showBeaconNews ? (
    <div>
      <h5 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded bg-[#15736d] text-white"
          aria-hidden
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
            <path d="M18 14h-8M15 18h-5M10 6h8v4h-8V6Z" />
          </svg>
        </span>
        BEACON news
      </h5>
      {beaconNews.length === 0 ? (
        <p className="text-sm text-gray-500">No BEACON reports for this country in the current filters.</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {beaconNews.map((item) => {
            const href = item.url || undefined;
            const Wrapper: React.ElementType = href ? 'a' : 'div';
            const wrapperProps = href
              ? { href, target: '_blank', rel: 'noopener noreferrer' }
              : {};
            return (
              <Wrapper
                key={item.id}
                {...wrapperProps}
                className="block rounded border border-gray-200 bg-gray-50 px-2.5 py-2 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start gap-2">
                  {item.diseaseCode ? (
                    <span
                      className="mt-0.5 text-[10px] font-bold uppercase tracking-wide px-1 py-0.5 rounded text-white shrink-0"
                      style={{ backgroundColor: getDiseaseColor(item.diseaseCode) }}
                    >
                      {item.diseaseCode}
                    </span>
                  ) : null}
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-gray-900 leading-snug">
                      {item.title || 'Untitled report'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {formatNewsDate(item.publishedAt || item.eventDate)}
                    </div>
                    {item.summary ? (
                      <p className="text-[11px] text-gray-600 mt-1 line-clamp-2">{item.summary}</p>
                    ) : null}
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </div>
      )}
    </div>
  ) : null;

  const hasFast = rowsByPeriod.length > 0;
  const hasInfur = infurPoints.length > 0;

  if (!hasFast && !hasInfur) {
    return (
      <div className="p-4 space-y-4 w-full min-w-[280px] max-w-md">
        <div>
          <h4 className="text-lg font-bold text-gray-800">{displayName}</h4>
          <p className="text-sm text-gray-600 mt-1">
            No FAST or {WAHIS_INFUR_LABEL} records for this country in the current situation (
            {periodLabel}).
          </p>
        </div>
        {newsSection}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 w-full min-w-[280px] max-w-md">
      <div>
        <h4 className="text-lg font-bold text-gray-800">{displayName}</h4>
        {region && <p className="text-sm text-gray-600">Region: {region}</p>}
        <p className="text-xs text-gray-500 mt-1">Current situation · {periodLabel}</p>
      </div>

      {hasFast &&
        rowsByPeriod.map(({ key, rows }) => (
          <div key={key} className="space-y-2">
            <h5 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">
              FAST {key}
            </h5>
            {rows.map((row) => {
              const outbreaks = parseCount(row.Outbreaks);
              const doses = parseCount(row.Vaccination_Doses);
              const vaccOn = Number(row.Vaccination) === 1 || (doses != null && doses > 0);
              const survOn = Number(row.Surveillance) === 1;
              const color = getDiseaseColor(row.Disease);

              return (
                <div
                  key={`${key}-${row.Disease}-${row.id}`}
                  className="rounded border border-gray-200 bg-white px-2.5 py-2"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-white"
                      style={{ backgroundColor: color }}
                    >
                      {row.Disease}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-1 text-xs text-gray-700">
                    <div>
                      <span className="font-medium text-gray-800">Outbreaks:</span>{' '}
                      {outbreaks == null ? 'Not reported' : outbreaks.toLocaleString()}
                    </div>
                    <NoteBlock label="Outbreak notes" text={row.Outbreak_Description} />

                    <div className="pt-1">
                      <span className="font-medium text-gray-800">Vaccination:</span>{' '}
                      {!vaccOn
                        ? 'Not reported'
                        : doses != null && doses > 0
                          ? `${doses.toLocaleString()} doses`
                          : 'In place (no dose numbers)'}
                    </div>
                    <NoteBlock label="Vaccination notes" text={row.Vaccination_Description} />

                    <div className="pt-1">
                      <span className="font-medium text-gray-800">Surveillance:</span>{' '}
                      {survOn ? 'Reported' : 'Not reported'}
                    </div>

                    <NoteBlock
                      label="Epidemiological information"
                      text={row.Epidemiological_Information}
                    />
                    <NoteBlock label="Other notes" text={row.Other_Info} />
                    {row.Source ? (
                      <div className="text-[11px] text-gray-400 pt-1">Source: {row.Source}</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

      {hasInfur && (
        <div className="space-y-2">
          <h5 className="text-sm font-semibold text-gray-800 border-b border-gray-200 pb-1">
            {WAHIS_INFUR_LABEL} outbreaks ({infurPoints.length})
          </h5>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {infurPoints.map((p, idx) => {
              const color = getDiseaseColor(p.disease);
              const key = `${p.outbreakId ?? 'x'}-${p.speciesName ?? ''}-${p.latitude}-${p.longitude}-${idx}`;
              return (
                <div key={key} className="rounded border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded text-white"
                      style={{ backgroundColor: color }}
                    >
                      {p.disease}
                    </span>
                    {p.eventStatus ? (
                      <span className="text-gray-600">{p.eventStatus}</span>
                    ) : null}
                  </div>
                  <div className="text-gray-800 font-medium">
                    {p.location || p.adminDivision || displayName}
                  </div>
                  {p.outbreakStartDate ? (
                    <div className="text-gray-600 mt-0.5">
                      Start: {p.outbreakStartDate}
                      {p.outbreakEndDate ? ` → ${p.outbreakEndDate}` : ''}
                    </div>
                  ) : null}
                  {p.speciesName ? (
                    <div className="text-gray-600">
                      Species: {p.speciesName}
                      {p.epiUnitType ? ` · ${p.epiUnitType}` : ''}
                    </div>
                  ) : null}
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-1 text-gray-600">
                    <div>Cases: <strong>{p.cases ?? '—'}</strong></div>
                    <div>Deaths: <strong>{p.deaths ?? '—'}</strong></div>
                    <div>Susceptible: <strong>{p.susceptible ?? '—'}</strong></div>
                    <div>Vaccinated: <strong>{p.vaccinated ?? '—'}</strong></div>
                  </div>
                  {p.reason ? <p className="text-gray-500 mt-1">{p.reason}</p> : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {newsSection}
    </div>
  );
};

export default CountryNowSummaryPanel;
